import React, { useCallback, useContext, useMemo, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'LuxKit/Typography/Heading'
import Hr from 'components/Common/Hr'
import Image from 'components/Common/Image'
import MarkdownRender from 'LuxKit/MarkdownRender'
import LineClockIcon from 'LuxKit/Icons/line/LineClockIcon'
import LineGlobeIcon from 'LuxKit/Icons/line/LineGlobeIcon'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import Pane from 'components/Common/Pane'
import TextButton from 'LuxKit/Button/TextButton'
import { pluralizeToString } from 'lib/string/pluralize'
import { getCruiseCountriesByItinerary, getCruiseOfferPriceVariation, getCruisePortsByItinerary, offerHasValidDepartures } from 'lib/cruises/cruiseUtils'
import CruiseSupportButton from './CruiseSupportButton'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Group from 'components/utils/Group'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import CruiseInclusionList from 'components/Cruises/CruiseInclusion/CruiseInclusionList'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import ModalContext from 'contexts/ModalContext'
import CruisesDiscountTsAndCsModal from 'components/Cruises/CruisesDiscountTsAndCsModal'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import ContactUSP from 'components/OfferPage/ContactUSP'
import CruiseLoyaltyPoints from 'components/Cruises/CruiseLoyaltyPoints'
import TextLink from 'LuxKit/TextLink'
import Divider from 'LuxKit/Divider'
import useCruiseOfferView from 'hooks/Cruise/useCruiseOfferView'
import * as Analytics from 'analytics/analytics'
import { useQsQueryParams } from 'hooks/useQueryParams'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'

const Root = styled.div`
  display: grid;
  grid-template-areas:
      "top-bar top-bar top-bar"
      ". . ."
      "title title ."
      ". . ."
      "details details details"
      ". . ."
      "promo promo promo"
      ". . ."
      "overview overview overview"
      ". . ."
      "description description description"
      ". . ."
      "journey journey journey"
      ". . ."
      "top-support-banner top-support-banner top-support-banner"
      ". . ."
      "bottom-support-banner bottom-support-banner bottom-support-banner"
      ;
  grid-template-columns: 1fr;
  grid-template-rows:
      auto
      ${rem(8)}
      auto
      ${rem(8)}
      auto
      ${rem(12)}
      auto
      ${rem(12)}
      auto
      ${rem(16)}
      auto
      ${rem(16)}
      auto
      ${rem(16)}
      auto
      ${rem(16)}
      auto;
  max-width: 100%;


  ${mediaQueryUp.tablet} {
    grid-template-areas:
      "top-bar . overview"
      ". . overview"
      "title . overview"
      ". . overview"
      "details . overview"
      ". . overview"
      "promo . overview"
      ". . overview"
      "description . overview"
      ". . overview"
      "journey . overview"
      ". . overview"
      "top-support-banner . overview"
      "bottom-support-banner . ."; /* to create a fake empty area to push content up */
    grid-template-columns: 1fr ${rem(32)} ${rem(400)};
    grid-template-rows:
      auto
      ${rem(8)}
      auto
      ${rem(12)}
      auto
      ${rem(12)}
      auto
      ${rem(24)}
      auto
      ${rem(16)}
      auto
      ${rem(16)}
      auto
      ${rem(24)}
      auto
      auto;
  }
`

const OverviewArea = styled.div`
  grid-area: overview;
`

const JourneyArea = styled.div`
  grid-area: journey;
`

const ContactUSPArea = styled.div`
  padding: ${rem(20)} 0;
  border-top: 1px solid ${props => props.theme.palette.neutral.default.six};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};
  grid-area: top-support-banner;
`

const TopBarArea = styled.div`
  grid-area: top-bar;

  ${mediaQueryUp.tablet} {
    flex-direction: row;
  }
`

const TitleArea = styled.div`
  grid-area: title;
`

const OperatorLogoImage = styled(Image)`
  max-width: 100%;
  max-height: 100%;
`

const OperatorLogoContainer = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: flex;
    align-self: center;
    flex-shrink: 0;
    max-height: ${rem(75)};
    width: ${rem(150)};
  }
`

const DetailItemsContainer = styled.div`
  display: flex;
  grid-area: details;
  flex-direction: column;
  gap: ${rem(2)};

  &:empty {
    display: none;
  }

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    gap: ${rem(16)};
  }
`

const OverviewPane = styled(Pane)`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  padding: ${rem(24)} 0;
  border-top: 1px solid ${props => props.theme.palette.neutral.default.six};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};

  ${mediaQueryUp.tablet} {
    padding: ${rem(24)};
    border-radius: ${props => props.theme.borderRadius.S};
    border: 1px solid ${props => props.theme.palette.neutral.default.five};
    box-shadow: ${props => props.theme.shadow.flat.small};
  }
`

const OverviewPaneDetailItemsContainer = styled.div`
  display: grid;
  gap: ${rem(8)};
  grid-auto-flow: row;
  grid-template-columns: repeat(2, 1fr);

  &.three-columns {
    display: flex;
    gap: ${rem(24)};
  }
`

const OverviewPanePriceContainer = styled.div`
  display: flex;
  flex-direction: column;

  > :last-child {
    padding-top: ${rem(8)};
  }
`

const PromotionArea = styled.div`
  grid-area: promo;
`

const ShipDescriptionArea = styled.div`
  grid-area: description;
`

export const TextLinkVendor = styled(TextLink)`
  padding-left: ${rem(4)};
`

interface Props {
  offer: App.CruiseOffer
  showPackageOptions: () => void
}

function CruiseOverview(props: Props) {
  const {
    offer,
    showPackageOptions,
  } = props
  const {
    standardInclusions,
    luxPlusInclusions,
  } = useCruiseOfferView({ offer })

  const { departurePort, returnPort, name, ship, itinerary } = offer
  const { lowestOverallPriceDetails } = offer.mainDepartureDetails
  const { discountPills } = lowestOverallPriceDetails
  const shipInfoDescription = ship.shipInfo?.find(info => info.dataTypeName === 'Summary')?.description
  const offerDescription = offer.description || (shipInfoDescription || offer.destinationDescription)?.replace(/(<([^>]+)>)/gi, '')

  // operator
  const operatorName = offer.cruiseLine.name
  const operatorLogo = offer.cruiseLine.imageId

  const hasTheSameStartAndEndPort = departurePort === returnPort

  const ports = useMemo(() => getCruisePortsByItinerary(itinerary), [itinerary])
  const countries = useMemo(() => getCruiseCountriesByItinerary(itinerary), [itinerary])

  const returnPortCity = useMemo(() => returnPort?.split(',')?.[0], [returnPort])
  const departurePortCity = useMemo(() => departurePort?.split(',')?.[0], [departurePort])

  const hasValidDepartureDates = useMemo(() => offerHasValidDepartures(offer), [offer])

  const {
    primaryPrice,
    secondaryPrice,
  } = getCruiseOfferPriceVariation(offer, lowestOverallPriceDetails)

  const showModal = useContext(ModalContext)
  const openDiscountTsAndCsModal = useCallback(() => {
    showModal(<CruisesDiscountTsAndCsModal />)
  }, [showModal])

  const shouldTrackPrice = lowestOverallPriceDetails && hasValidDepartureDates && !!primaryPrice?.total
  const priceTrackedRef = useRef(false)
  const { searchId } = useQsQueryParams<{ searchId?: string }>()
  useEffect(() => {
    if (shouldTrackPrice && searchId && offer?.id && !priceTrackedRef.current) {
      Analytics.trackClientEvent({
        subject: `searchId:${searchId}_offerId:${offer.id}`,
        action: primaryPrice.total.toString(),
        category: 'price_accuracy',
        type: 'operational',
      })
      priceTrackedRef.current = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrackPrice, primaryPrice?.total, offer?.id])

  return (
    <Root className="OfferIntro">
      <TitleArea className="title-area" data-testid="cruise-offer-overview">
        <Group direction="horizontal" gap={8} horizontalAlign="space-between">
          <Heading as="h1" variant="heading2">
            {name}
          </Heading>
          {operatorLogo && <OperatorLogoContainer>
            <OperatorLogoImage
              id={operatorLogo}
              dpr={2}
              quality="eco"
              width={150}
              fit="center"
              alt={operatorName}
              aria-hidden="true"
            />
          </OperatorLogoContainer>}
        </Group>
      </TitleArea>

      <DetailItemsContainer>
        {!!operatorName && <BodyText variant="medium" colour="neutral-two">
          Cruise line: <b>{operatorName}</b>
        </BodyText>}

        <Divider direction="vertical" kind="secondary" />

        {!!ship && <BodyText variant="medium" colour="neutral-two">
          Cruise ship: <b>{ship.name}</b>
        </BodyText>}
      </DetailItemsContainer>

      <PromotionArea>
        <CruiseInclusionList
          offer={offer}
          evergreenInclusions={offer.evergreenInclusions}
          standardInclusions={standardInclusions}
          luxPlusInclusions={luxPlusInclusions}
          gap={16}
        />
      </PromotionArea>

      <ShipDescriptionArea>
        {!!offerDescription && <>
          <CSSBreakpoint max="mobile">
            <LargeContentToggle
              maxHeight={100}
              showMoreText="Read more"
              showLessText="Read less"
            >
              <MarkdownRender
                fontSize="medium"
                fontWeight="normal"
                type="compact"
                content={offerDescription}
              />
            </LargeContentToggle>
          </CSSBreakpoint>

          <CSSBreakpoint min="tablet">
            <MarkdownRender
              fontSize="medium"
              fontWeight="normal"
              type="compact"
              content={offerDescription}
            />
          </CSSBreakpoint>
        </>}
      </ShipDescriptionArea>

      <TopBarArea className="top-bar-area">
        <BodyTextBlock variant="medium">
          {hasTheSameStartAndEndPort && <span>
            Starts and ends in <b>&nbsp;{returnPortCity}</b>
          </span>}
          {!hasTheSameStartAndEndPort && <span>
            Starts in <b>&nbsp;{departurePortCity}</b>, ends in <b>&nbsp;{returnPortCity}</b>
          </span>}
        </BodyTextBlock>
      </TopBarArea>

      <OverviewArea>
        <OverviewPane>
          <Heading variant="heading5">
            Overview
          </Heading>
          <OverviewPaneDetailItemsContainer>
            <BodyTextBlock variant="medium" startIcon={<LineClockIcon />}>
              {pluralizeToString('night', offer.duration)}
            </BodyTextBlock>
            {!!ports?.length && <BodyTextBlock variant="medium" startIcon={<LineMapIcon />}>
              {pluralizeToString('port', ports.length)}
            </BodyTextBlock>}
            {!!countries?.length && <BodyTextBlock variant="medium" startIcon={<LineGlobeIcon />}>
              {pluralizeToString('country', countries.length)}
            </BodyTextBlock>}
          </OverviewPaneDetailItemsContainer>

          <CSSBreakpoint only="mobile">
            <CruiseLoyaltyPoints
              offer={offer}
              calculationType="estimate"
            />
          </CSSBreakpoint>

          <CSSBreakpoint min="tablet">
            <Group direction="vertical" gap={16}>
              <Hr colour="light-grey" />

              <Group direction="vertical" gap={16}>
                <div>
                  {lowestOverallPriceDetails && hasValidDepartureDates && <OverviewPanePriceContainer>
                    <Group direction="vertical" gap={4}>
                      <div>
                        <PriceRowPriceCaption>From</PriceRowPriceCaption>
                        <PriceRowPrice
                          size="L"
                          price={primaryPrice.total}
                          saleUnit={primaryPrice.type}
                        />
                      </div>
                      {secondaryPrice && <PriceRowCaption>
                        <FormatCurrency value={secondaryPrice.total} />/{secondaryPrice.type} · Twin Share
                      </PriceRowCaption>}
                    </Group>
                    {discountPills && <PriceRowValueDiscountWithCaption
                      size="L"
                      originalValue={discountPills.price}
                      discountPercentage={discountPills.discountPercentage}
                      onInfoIconClick={openDiscountTsAndCsModal}
                    />}
                    <PriceRowAgentHubCommission
                      size="L"
                      offerId={offer.id}
                      offer={offer}
                      productType={offer.productType}
                    />
                  </OverviewPanePriceContainer>}
                </div>

                <CruiseLoyaltyPoints
                  offer={offer}
                  calculationType="estimate"
                />
              </Group>

              <Group direction="vertical" gap={12}>
                {hasValidDepartureDates && <TextButton
                  kind="primary"
                  onClick={showPackageOptions}
                  fit="full-width"
                >
                  Check availability
                </TextButton>}

                {!hasValidDepartureDates && <Group direction="vertical" gap={12}>
                  <BodyText variant="large" weight="bold" align="center">
                    This offer has sold out
                  </BodyText>
                  <TextButton kind="secondary" to="/cruises">Browse all cruise offers</TextButton>
                </Group>}

                <CruiseSupportButton />
              </Group>
            </Group>
          </CSSBreakpoint>
        </OverviewPane>
      </OverviewArea>

      <JourneyArea>
        <BodyText variant="medium" weight="normal">
          Your journey takes you to: {' '}
          <BodyText variant="medium" weight="bold" as="span">
            {ports.join(' - ')}
          </BodyText>
        </BodyText>
      </JourneyArea>

      <ContactUSPArea>
        <ContactUSP />
      </ContactUSPArea>
    </Root>
  )
}

export default CruiseOverview
